/**
 * Brand Management Helper
 * Handles brand detection and storage for Cloom and RoomAI
 * Requirements: 4_2, 4_3
 */

const BRAND_STORAGE_KEY = 'cloom_brand';
const VALID_BRANDS = ['cloom', 'roomai'];
const DEFAULT_BRAND = 'cloom';

/**
 * Detects brand from URL path or query parameter
 * Supports:
 * - Path-based: /roomai or /#/roomai
 * - Parameter-based: ?brand=roomai or /#/?brand=roomai
 * @returns {string} 'roomai' or 'cloom'
 */
export function detectBrandFromUrl() {
  try {
    // Check path-based routing (both pathname and hash)
    const pathname = window.location.pathname;
    const hash = window.location.hash;

    // Check for /roomai in pathname or /#/roomai in hash
    if (pathname.includes('/roomai') || hash.includes('/roomai')) {
      return 'roomai';
    }

    // Check query parameters in both URL and hash
    const urlParams = new URLSearchParams(window.location.search);
    const brandParam = urlParams.get('brand');

    if (brandParam && VALID_BRANDS.includes(brandParam.toLowerCase())) {
      return brandParam.toLowerCase();
    }

    // Check query parameters in hash (after #)
    if (hash.includes('?')) {
      const hashQuery = hash.split('?')[1];
      const hashParams = new URLSearchParams(hashQuery);
      const hashBrandParam = hashParams.get('brand');

      if (hashBrandParam && VALID_BRANDS.includes(hashBrandParam.toLowerCase())) {
        return hashBrandParam.toLowerCase();
      }
    }

    return DEFAULT_BRAND;
  } catch (error) {
    console.error('Error detecting brand from URL:', error);
    return DEFAULT_BRAND;
  }
}

/**
 * Gets the current brand from URL or session storage
 * Priority: sessionStorage (after login) > URL (login page) > default
 * Note: sessionStorage takes priority as it's set from tenant data after login
 * @returns {string} 'roomai' or 'cloom'
 */
export function getCurrentBrand() {
  try {
    // First priority: Check sessionStorage (set from tenant data after login)
    const sessionBrand = sessionStorage.getItem(BRAND_STORAGE_KEY);
    if (sessionBrand && VALID_BRANDS.includes(sessionBrand)) {
      return sessionBrand;
    }

    // Second priority: Check URL for brand (used on login page before tenant loads)
    const urlBrand = detectBrandFromUrl();
    if (urlBrand !== DEFAULT_BRAND) {
      return urlBrand;
    }

    return DEFAULT_BRAND;
  } catch (error) {
    console.error('Error getting current brand:', error);
    return DEFAULT_BRAND;
  }
}

/**
 * Saves brand preference to session storage (from tenant data)
 * @param {string} brand - 'roomai' or 'cloom'
 */
export function saveBrandToStorage(brand) {
  try {
    if (!VALID_BRANDS.includes(brand)) {
      console.warn(`Invalid brand: ${brand}. Using default: ${DEFAULT_BRAND}`);
      brand = DEFAULT_BRAND;
    }

    // Only save to sessionStorage (not localStorage)
    // Brand persists only for the current session
    sessionStorage.setItem(BRAND_STORAGE_KEY, brand);
  } catch (error) {
    console.error('Error saving brand to storage:', error);
  }
}

/**
 * Clears brand from storage (used on logout)
 */
export function clearBrandFromStorage() {
  try {
    sessionStorage.removeItem(BRAND_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing brand from storage:', error);
  }
}

/**
 * Checks if current brand is RoomAI
 * @returns {boolean}
 */
export function isRoomAI() {
  return getCurrentBrand() === 'roomai';
}

/**
 * Checks if current brand is Cloom
 * @returns {boolean}
 */
export function isCloom() {
  return getCurrentBrand() === 'cloom';
}

/**
 * Gets brand-specific configuration
 * @returns {object} Brand configuration
 */
export function getBrandConfig() {
  const brand = getCurrentBrand();

  const configs = {
    cloom: {
      name: 'Cloom',
      logoPath: '/assets/img/cloom-logo.png',
      faviconPath: '/assets/img/favicon.png',
      pageTitle: 'Cloom',
    },
    roomai: {
      name: 'RoomAI',
      logoPath: '/assets/img/roomai-logo.png',
      faviconPath: '/assets/img/roomai-favicon.jpg',
      pageTitle: 'RoomAI',
    },
  };

  return configs[brand] || configs.cloom;
}

export default {
  detectBrandFromUrl,
  getCurrentBrand,
  saveBrandToStorage,
  clearBrandFromStorage,
  isRoomAI,
  isCloom,
  getBrandConfig,
};
